import React from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { toast } from 'sonner';

const PLANS = [
  {
    name: 'Scribe',
    price: '$0',
    description: 'Perfect for casual editorial work and basic manuscripts.',
    features: [
      '3 Documents / month',
      'Standard AI Engine',
      'Basic Export (HTML)',
      'Community Support'
    ],
    button: 'Current Plan',
    current: true
  },
  {
    name: 'Master Editor',
    price: '$12',
    period: '/ month',
    description: 'For professionals requiring high-density prose and unlimited archives.',
    features: [
      'Unlimited Documents',
      'High-Density AI Models',
      'Advanced Export (PDF, DOCX)',
      'Priority Scriptorium Support',
      'Collaborator Seats (5)',
      'Historical Epoch Archives'
    ],
    button: 'Upgrade to Master',
    popular: true,
    current: false
  },
  {
    name: 'Grand Guild',
    price: '$49',
    period: '/ month',
    description: 'Enterprise-grade editorial power for teams and publishing houses.',
    features: [
      'Everything in Master',
      'Unlimited Collaborators',
      'Custom Style Guides',
      'API Access',
      'Advanced Security Vaults',
      'Direct Master Scribe Support'
    ],
    button: 'Contact Sales',
    current: false
  }
];

export function UpgradePage() {
  const handleUpgrade = (planName: string) => {
    if (planName === 'Scribe') return;
    toast.info(`Initiating upgrade to ${planName}...`);
  };

  return (
    <AppLayout>
      <div className="flex-1 bg-surface-container-low overflow-y-auto">
        <header className="bg-surface border-b border-outline-variant px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto text-center">
             <span className="font-label-caps text-label-caps text-primary uppercase tracking-widest mb-2 block">Ascension</span>
             <h1 className="font-h1 text-h1 text-on-surface mb-4 italic">Elevate Your Editorial Power</h1>
             <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl mx-auto">
               Choose a plan that matches your structural requirements and archival density.
             </p>
          </div>
        </header>

        <main className="px-4 md:px-margin-page py-12 pb-24">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {PLANS.map((plan) => (
              <div 
                key={plan.name} 
                className={cn(
                  "relative flex flex-col p-8 bg-surface border rounded-2xl transition-all hover:shadow-2xl hover:-translate-y-1",
                  plan.popular ? "border-primary shadow-lg ring-1 ring-primary/20" : "border-outline-variant shadow-sm"
                )}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-on-primary px-4 py-1 rounded-full font-label-caps text-[10px] uppercase tracking-widest shadow-md">
                    Most Recommended
                  </div>
                )}

                <div className="mb-8">
                  <h3 className="font-h3 text-h3 text-on-surface mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-h1 text-on-surface">{plan.price}</span>
                    {plan.period && <span className="text-on-surface-variant font-body-md">{plan.period}</span>}
                  </div>
                  <p className="text-on-surface-variant font-body-md text-sm leading-relaxed italic">
                    {plan.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-on-surface-variant font-body-md text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => handleUpgrade(plan.name)}
                  disabled={plan.current}
                  className={cn(
                    "w-full py-4 rounded-xl font-label-caps text-label-caps uppercase tracking-widest transition-all",
                    plan.current 
                      ? "bg-surface-container-high text-on-surface-variant cursor-default" 
                      : plan.popular
                        ? "bg-primary text-on-primary shadow-lg hover:brightness-110 active:scale-[0.98]"
                        : "bg-surface-container-highest text-on-surface hover:bg-surface-container-high border border-outline-variant"
                  )}
                >
                  {plan.button}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-16 max-w-2xl mx-auto bg-surface-container/30 border border-outline-variant/30 rounded-2xl p-8 text-center italic">
             <p className="font-body-md text-on-surface-variant">
               "Every great treatise deserves the density of a master scribe's hand."
             </p>
             <span className="font-label-caps text-[10px] uppercase tracking-widest mt-2 block opacity-50">— The Grand Librarian</span>
          </div>
        </main>
      </div>
    </AppLayout>
  );
}
